import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { ReactElement } from "react";
import { BlogArchiveContent } from "~/components/blog/BlogArchiveContent";
import { MainLayout } from "~/components/layouts/MainLayout";
import {
  getBlogArchiveLinkTags,
  getBlogArchivePageMetaTitle,
  type BlogArchivePageData,
} from "~/lib/blogArchive";
import { NextPageWithLayout } from "~/pages/_app";

type Props = BlogArchivePageData;

function getBaseUrl() {
  return (process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl").replace(
    /\/+$/,
    ""
  );
}

function getPageDescription(page: number) {
  return `Archiwum poradnika Inspektomat, strona ${page}. Artykuły o bezpieczeństwie instalacji elektrycznych, gazowych i przeciwpożarowych.`;
}

const BlogPaginatedPage: NextPageWithLayout<Props> = (props) => (
  <BlogArchiveContent {...props} />
);

BlogPaginatedPage.getLayout = (page: ReactElement<Props>) => (
  <MainLayout>
    <NextSeo
      title={getBlogArchivePageMetaTitle(page.props.currentPage)}
      description={getPageDescription(page.props.currentPage)}
      additionalLinkTags={getBlogArchiveLinkTags(
        getBaseUrl(),
        page.props.currentPage,
        page.props.totalPages
      )}
    />
    {page}
  </MainLayout>
);

export const getStaticPaths: GetStaticPaths = async () => {
  const { getBlogArchiveTotalPages } = await import("~/lib/blogArchiveData");
  const totalPages = getBlogArchiveTotalPages();
  const paths = Array.from({ length: Math.max(totalPages - 1, 0) }, (_, index) => ({
    params: { page: String(index + 2) },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { getBlogArchivePageData } = await import("~/lib/blogArchiveData");
  const pageParam = Array.isArray(params?.page) ? params.page[0] : params?.page;
  const pageNumber = Number(pageParam);

  if (!Number.isInteger(pageNumber) || pageNumber < 2) {
    return { notFound: true };
  }

  const data = getBlogArchivePageData(pageNumber);

  if (!data) {
    return { notFound: true };
  }

  return { props: data };
};

export default BlogPaginatedPage;
